import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInputsChanged } from '../ui/use-inputs-changed.js';
import { CreditCard, Loader2 } from 'lucide-react';
import { Alert, Badge, Button, CheckboxGroup, Combobox, DescriptionList, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, Input, MultiCombobox, SectionDivider, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, DateTimeInput, } from '../ui/index.js';
import { alreadyRecordedCardId, cardErrorDetail, cardSaveFailure, encodedValidFromIso, writeCardImage } from './writeCard.js';
import { chipTypeForCarrier } from '../../nfcagent/types.js';
import { useReaderPrecondition } from '../../hooks/useReaderPrecondition.js';
import { useCardRoles, useLockGroups, useLocks, useGetIssueCardPayload, useGetReplaceCardPayload, useIssueCard, useReplaceCard, useRooms, } from '../../hooks/useApi.js';
import { CHIP_TYPE_LABELS, getApiClient } from '../../api/client.js';
import { useTranslation } from '../../i18n/index.js';
export const ISSUE_CARD_MODAL_OPEN_EVENT = 'bms:issue-card-modal-open-change';
function publishIssueCardModalOpen(open) {
    if (typeof window === 'undefined')
        return;
    const issueWindow = window;
    issueWindow.__bmsIssueCardModalCount = Math.max(0, (issueWindow.__bmsIssueCardModalCount ?? 0) + (open ? 1 : -1));
    window.dispatchEvent(new CustomEvent(ISSUE_CARD_MODAL_OPEN_EVENT, {
        detail: { open: issueWindow.__bmsIssueCardModalCount > 0 },
    }));
}
/**
 * Count a card-writing dialog as open while `open` is true, so `CardTapHandler`
 * leaves a tap to the dialog instead of raising its own toast. Every dialog that
 * writes cards (this one, the User UI's check-in) takes part.
 */
export function useCardDialogOpenSignal(open) {
    useEffect(() => {
        if (!open)
            return undefined;
        publishIssueCardModalOpen(true);
        return () => publishIssueCardModalOpen(false);
    }, [open]);
}
/**
 * The `cards.*` key for what the operator is told when a card whose write
 * outcome is unknown was recorded — never English text; a caller translates
 * with `t(UNKNOWN_OUTCOME_SAVED)`, the same pattern `encodeErrorKey()` uses.
 */
export const UNKNOWN_OUTCOME_SAVED = 'cards.unknownOutcomeSaved';
const EMPTY_VALUES = {
    linkedRoomIds: [],
    roomLockCodes: [],
    extraLockCodes: [],
    extraGroupCodes: [],
};
const CARD_TYPE_LABEL_KEYS = {
    guest: 'cards.guest',
    staff: 'cards.staff',
    service: 'cards.service',
    master: 'cards.master',
    temporary: 'cards.temporary',
};
function isSelectableLock(lock) {
    return lock.status !== 'decommissioned';
}
function defaultCardType(mode, context) {
    if (context?.defaultCardType)
        return context.defaultCardType;
    if (mode === 'replace' && context?.card?.cardType)
        return context.card.cardType;
    if (mode === 'room')
        return 'guest';
    return 'staff';
}
/** Returns a `cards.*` key, never English — call sites translate with `t(...)`. */
function titleForMode(mode) {
    switch (mode) {
        case 'room': return 'cards.titleRoom';
        case 'lock': return 'cards.titleLock';
        case 'lockGroup': return 'cards.titleLockGroup';
        case 'role': return 'cards.titleRole';
        case 'replace': return 'cards.titleReplace';
        case 'standalone': return 'cards.titleStandalone';
    }
}
/**
 * The latest instant a card may be valid until: midnight local time one year
 * out, matching the calendar bound the antd `disabledDate` rule enforced.
 */
function latestValidUntil(now = new Date()) {
    return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
}
/** ISO instant -> the `datetime-local` control's local wall-clock value. */
function isoToLocalInput(iso) {
    if (!iso)
        return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return '';
    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
/** `datetime-local` wall-clock value -> ISO instant, or undefined when cleared. */
function localInputToIso(value) {
    if (!value)
        return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
export const IssueCardModal = ({ open, onClose, mode, context, roleTemplateOnly = false, onIssued, onEncodedIssue, onNotify, }) => {
    const { t } = useTranslation();
    const [values, setValues] = useState(EMPTY_VALUES);
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [pendingPersistence, setPendingPersistence] = useState(null);
    const [initializedContextKey, setInitializedContextKey] = useState(null);
    const precondition = useReaderPrecondition(open);
    const issueCard = useIssueCard();
    const replaceCard = useReplaceCard();
    const getIssuePayload = useGetIssueCardPayload();
    const getReplacePayload = useGetReplaceCardPayload();
    const { data: rooms = [] } = useRooms({ enabled: open });
    const { data: roles = [] } = useCardRoles();
    const { data: locks = [] } = useLocks();
    const { data: lockGroups = [] } = useLockGroups();
    const setValue = useCallback((key, value) => {
        setValues(current => (Object.is(current[key], value) ? current : { ...current, [key]: value }));
        setErrors(current => (current[key] ? { ...current, [key]: undefined } : current));
    }, []);
    const notify = useCallback((notice) => {
        onNotify?.(notice);
    }, [onNotify]);
    const enteredCardUid = values.cardUid;
    const selectedChipType = values.chipType;
    const primaryRoomCode = values.roomCode;
    // With `roleTemplateOnly` the scope is not a choice: the only card this
    // dialog may compose is one whose access comes entirely from a role template.
    const issueScope = roleTemplateOnly ? 'role' : values.issueScope;
    const lockCode = values.lockCode;
    const lockGroupCode = values.lockGroupCode;
    // The fixed carrier rule: guest and temporary cards on either carrier, every
    // other type on EV2 only. BMS-spec docs/access/cards.md §Carrier rule.
    const detectedChipType = useMemo(() => chipTypeForCarrier(precondition.lastCard?.card.carrier), [precondition.lastCard?.card.carrier]);
    const roomOptions = useMemo(() => {
        return rooms.map(room => ({ value: room.roomCode, label: `${room.roomName} (${room.roomCode})` }));
    }, [rooms]);
    const linkedRoomOptions = useMemo(() => {
        return rooms
            .filter(room => room.roomCode !== primaryRoomCode)
            .map(room => ({ value: room.id, label: `${room.roomName} (${room.roomCode})` }));
    }, [rooms, primaryRoomCode]);
    const lockOptions = useMemo(() => {
        return locks.filter(isSelectableLock).map(lock => ({
            value: lock.lockCode,
            label: `${lock.lockName}${lock.roomName ? ` (${lock.roomName})` : ''}`,
            lock,
        }));
    }, [locks]);
    const lockGroupOptions = useMemo(() => {
        return lockGroups.map(group => ({
            value: group.groupCode,
            label: group.groupName,
            group,
        }));
    }, [lockGroups]);
    const selectedLock = useMemo(() => lockOptions.find(option => option.value === lockCode)?.lock ?? context?.lock ?? null, [context?.lock, lockCode, lockOptions]);
    const scopedRoomCode = useMemo(() => {
        if (mode === 'standalone') {
            return issueScope === 'room' ? primaryRoomCode : undefined;
        }
        if (mode === 'room') {
            return context?.room?.roomCode ?? primaryRoomCode;
        }
        return primaryRoomCode;
    }, [context?.room?.roomCode, issueScope, mode, primaryRoomCode]);
    // Fetch room-scoped locks from API using roomCode filter once scope is known.
    const { data: roomScopedLocks = [] } = useLocks(scopedRoomCode ? { roomCode: scopedRoomCode } : undefined);
    const isRoomScope = mode === 'room' || (mode === 'standalone' && issueScope === 'room');
    const scopedRoomLocks = useMemo(() => {
        // Use roomScopedLocks (fetched via API with roomCode filter) as primary source,
        // fallback to local filtering of all locks for backward compatibility.
        if (roomScopedLocks.length > 0)
            return roomScopedLocks.filter(isSelectableLock);
        if (!scopedRoomCode)
            return [];
        return locks.filter(lock => lock.roomCode === scopedRoomCode && isSelectableLock(lock));
    }, [roomScopedLocks, locks, scopedRoomCode]);
    // The entrance lock(s) are always included on a room card — checked and not unselectable.
    const entranceLockCodes = useMemo(() => scopedRoomLocks.filter(lock => lock.lockType === 'entrance').map(lock => lock.lockCode), [scopedRoomLocks]);
    const scopedRoomLockOptions = useMemo(() => {
        // Exclude entrance lock from checkbox list; it is always implicitly included server-side.
        // Only render additional room locks (minibar, safe, etc.).
        return scopedRoomLocks
            .filter(lock => lock.lockType !== 'entrance')
            .map(lock => ({
            value: lock.lockCode,
            label: `${lock.lockName} (${lock.lockCode})`,
        }));
    }, [scopedRoomLocks]);
    // Non-room locks (pool, parking, common areas …) that can be added to any card.
    const nonRoomLockOptions = useMemo(() => {
        return locks
            .filter(lock => !lock.roomCode && isSelectableLock(lock))
            .map(lock => ({ value: lock.lockCode, label: `${lock.lockName} (${lock.lockCode})`, lock }));
    }, [locks]);
    const selectedLockGroup = useMemo(() => lockGroupOptions.find(option => option.value === lockGroupCode)?.group ?? context?.lockGroup ?? null, [context?.lockGroup, lockGroupCode, lockGroupOptions]);
    useCardDialogOpenSignal(open);
    const initialValuesContextKey = useMemo(() => JSON.stringify({
        mode,
        defaultCardUid: context?.defaultCardUid ?? null,
        defaultChipType: context?.defaultChipType ?? null,
        cardUid: context?.card?.cardUid ?? null,
        holderName: context?.card?.holderName ?? context?.defaultHolderName ?? context?.roomGuestName ?? null,
        validUntil: context?.roomCheckoutExpected ?? context?.defaultValidUntil ?? null,
        visitId: context?.visitId ?? null,
        roleTemplateName: context?.role?.roleName ?? context?.card?.roleTemplateName ?? null,
        roomCode: context?.room?.roomCode ?? context?.card?.roomCode ?? null,
        lockCode: context?.lock?.lockCode ?? null,
        lockGroupCode: context?.lockGroup?.groupCode ?? null,
    }), [
        mode,
        context?.defaultCardUid,
        context?.defaultChipType,
        context?.card?.cardUid,
        context?.card?.holderName,
        context?.defaultHolderName,
        context?.roomGuestName,
        context?.roomCheckoutExpected,
        context?.defaultValidUntil,
        context?.visitId,
        context?.role?.roleName,
        context?.card?.roleTemplateName,
        context?.room?.roomCode,
        context?.card?.roomCode,
        context?.lock?.lockCode,
        context?.lockGroup?.groupCode,
    ]);
    // The three form adjustments below run while rendering, in this order, whenever
    // their inputs change (the order matters: initialisation replaces the room-lock
    // selection, and a tapped card overrides the initial UID).
    //
    // 1. Keep the room-lock selection valid as the room changes: always include the entrance
    // lock(s) and drop any selected code that is no longer in the current room scope.
    // Keyed by the lock codes: the fetched list is a fresh `[]` on every render until it answers.
    const roomLocksChanged = useInputsChanged([
        open,
        isRoomScope,
        scopedRoomLocks.map(lock => lock.lockCode).join('|'),
        entranceLockCodes.join('|'),
    ]);
    if (roomLocksChanged && open && isRoomScope) {
        const validCodes = new Set(scopedRoomLocks.map(lock => lock.lockCode));
        setValues(current => {
            const next = [...new Set([...entranceLockCodes, ...current.roomLockCodes.filter(code => validCodes.has(code))])];
            if (next.length === current.roomLockCodes.length && next.every(code => current.roomLockCodes.includes(code))) {
                return current;
            }
            return { ...current, roomLockCodes: next };
        });
    }
    // 2. Initialise the form once per opened context.
    if (!open) {
        if (initializedContextKey !== null)
            setInitializedContextKey(null);
    }
    else if (initializedContextKey !== initialValuesContextKey) {
        const initialCardType = defaultCardType(mode, context);
        setValues({
            cardUid: context?.defaultCardUid ?? undefined,
            chipType: context?.defaultChipType ?? undefined,
            cardType: initialCardType,
            holderName: context?.card?.holderName ?? context?.defaultHolderName ?? context?.roomGuestName ?? undefined,
            validUntil: context?.roomCheckoutExpected ?? context?.defaultValidUntil ?? undefined,
            roleTemplateName: context?.role?.roleName ?? context?.card?.roleTemplateName ?? undefined,
            roomCode: context?.room?.roomCode ?? context?.card?.roomCode ?? undefined,
            linkedRoomIds: context?.card?.linkedRoomIds ?? [],
            issueScope: mode === 'standalone' ? (roleTemplateOnly ? 'role' : 'room') : undefined,
            lockCode: context?.lock?.lockCode ?? undefined,
            roomLockCodes: mode === 'room' ? entranceLockCodes : [],
            extraLockCodes: [],
            extraGroupCodes: [],
            lockGroupCode: context?.lockGroup?.groupCode ?? undefined,
        });
        setErrors({});
        setSubmitError(null);
        setPendingPersistence(null);
        setInitializedContextKey(initialValuesContextKey);
    }
    const tappedCardUid = precondition.lastCard?.card.uid;
    // 3. A tapped card fills the UID and chip type. Idempotent on purpose: antd's
    // `setFieldValue` was a no-op when the value already matched, and these inputs
    // include a hook result whose identity a caller does not control. Returning
    // `current` unchanged is what keeps an unchanged tap from re-rendering itself forever.
    const tapChanged = useInputsChanged([detectedChipType, open, tappedCardUid]);
    if (tapChanged && open && tappedCardUid) {
        setValues(current => {
            const nextChipType = detectedChipType;
            if (current.cardUid === tappedCardUid && current.chipType === nextChipType)
                return current;
            return { ...current, cardUid: tappedCardUid, chipType: nextChipType };
        });
    }
    const submitPending = issueCard.isPending || replaceCard.isPending || getIssuePayload.isPending || getReplacePayload.isPending;
    const activeReader = precondition.readerSnapshot.activePiccReader;
    const submitReason = precondition.submitDisabledReason === 'access.readerPrecondition.cardUidMissing' && enteredCardUid
        ? undefined
        : precondition.submitDisabledReason
            ? t(precondition.submitDisabledReason)
            : (!selectedChipType ? t('cards.chipTypeNotDetected') : undefined);
    const canSubmit = !submitReason;
    const maxValidUntil = useMemo(() => latestValidUntil(), []);
    /**
     * The same requirements the antd `Form.Item` rules carried, in the same
     * order, so a refusal reads exactly as it did before the conversion.
     */
    const validate = useCallback(() => {
        const next = {};
        if (!values.cardUid)
            next.cardUid = t('cards.validation.cardUidRequired');
        if (!values.chipType)
            next.chipType = t('cards.validation.chipTypeRequired');
        if (!values.cardType)
            next.cardType = t('cards.validation.cardTypeRequired');
        if (mode === 'standalone') {
            const scope = roleTemplateOnly ? 'role' : values.issueScope;
            if (!scope)
                next.issueScope = t('cards.validation.issueScopeRequired');
            if (scope === 'room' && !values.roomCode)
                next.roomCode = t('cards.validation.roomRequired');
            if (scope === 'lock' && !values.lockCode)
                next.lockCode = t('cards.validation.lockRequired');
            if (scope === 'lockGroup' && !values.lockGroupCode)
                next.lockGroupCode = t('cards.validation.lockGroupRequired');
            if (scope === 'role' && !values.roleTemplateName)
                next.roleTemplateName = t('cards.validation.roleTemplateRequired');
        }
        else if (mode === 'room' && !(context?.room?.roomCode ?? values.roomCode)) {
            next.roomCode = t('cards.validation.roomRequired');
        }
        if (!values.validUntil) {
            next.validUntil = t('cards.validation.validUntilRequired');
        }
        else {
            const chosen = new Date(values.validUntil);
            if (Number.isNaN(chosen.getTime())) {
                next.validUntil = t('cards.validation.validUntilRequired');
            }
            else if (chosen.getTime() > maxValidUntil.getTime()) {
                next.validUntil = t('cards.validation.validUntilTooFar');
            }
        }
        return next;
    }, [context?.room?.roomCode, maxValidUntil, mode, roleTemplateOnly, t, values]);
    const fail = useCallback((level, text) => {
        setSubmitError(text);
        notify({ level, message: text });
    }, [notify]);
    const persistWrittenCard = useCallback(async (pending) => {
        let issued;
        try {
            if (onEncodedIssue && !pending.isReplacement) {
                await onEncodedIssue(pending.issueParams);
            }
            else {
                issued = pending.isReplacement
                    ? await replaceCard.mutateAsync({ cardId: pending.replacedCardId, params: pending.replaceParams })
                    : await issueCard.mutateAsync(pending.issueParams);
            }
        }
        catch (error) {
            const cardId = alreadyRecordedCardId(error);
            if (!cardId)
                throw error;
            issued = await getApiClient().getAccessCard(cardId);
        }
        notify(pending.outcomeUnknown
            ? { level: 'warning', message: t(UNKNOWN_OUTCOME_SAVED) }
            : { level: 'success', message: t('cards.issuedAndEncoded') });
        if (issued)
            onIssued?.(issued);
        setPendingPersistence(null);
        setValues(EMPTY_VALUES);
        onClose();
    }, [issueCard, notify, onClose, onEncodedIssue, onIssued, replaceCard, t]);
    const retryPersistence = useCallback(async () => {
        if (!pendingPersistence)
            return;
        setSubmitError(null);
        try {
            await persistWrittenCard(pendingPersistence);
        }
        catch (error) {
            const detail = cardErrorDetail(error, t, 'cards.databaseSaveFailedDefault');
            const failure = cardSaveFailure(t, error, detail, { outcomeUnknown: pendingPersistence.outcomeUnknown, stillFailing: true });
            // A refused write report cannot be cured by saving again: the card is encoded afresh.
            if (!failure.retrySave)
                setPendingPersistence(null);
            fail('error', failure.message);
        }
    }, [fail, pendingPersistence, persistWrittenCard, t]);
    const handleSubmit = async () => {
        const reader = precondition.readerSnapshot.activePiccReader;
        if (!reader) {
            fail('warning', t('cards.placeCardFirst'));
            return;
        }
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.values(validationErrors).some(Boolean)) {
            setSubmitError(null);
            return;
        }
        setSubmitError(null);
        const resolvedCardType = values.cardType ?? defaultCardType(mode, context);
        const resolvedRoomCode = mode === 'standalone'
            ? (issueScope === 'room' ? values.roomCode : undefined)
            : (values.roomCode ?? context?.room?.roomCode ?? context?.card?.roomCode ?? undefined);
        if (!values.cardUid || !values.chipType)
            return;
        let encodedSuccessfully = false;
        let writeOutcomeUnknown = false;
        try {
            const identity = {
                cardUid: values.cardUid,
                chipType: values.chipType,
                holderName: values.holderName || undefined,
                // Reuse this exact timestamp for payload preparation and persistence;
                // it is the separator used by offline revocation matching.
                validFrom: new Date().toISOString(),
                validUntil: values.validUntil,
                roleTemplateName: mode === 'standalone'
                    ? issueScope === 'role' ? values.roleTemplateName : undefined
                    : values.roleTemplateName || undefined,
                // A returned card still holds its revoked record; Edge retires it in the
                // same transaction and refuses a live one (BMS-spec cards.md, UID reuse).
                retirePreviousUid: true,
            };
            // Everything that composes access by hand, kept in one object so the
            // role-template-only path can omit the whole group. Spreading `{}` leaves
            // the keys ABSENT rather than present-and-empty, which is the difference
            // the server's raw-body check reads: `linkedRoomIds: []` is still sending
            // the field and is refused `403 permission_denied`.
            const composedAccess = roleTemplateOnly ? {} : {
                roomCode: resolvedRoomCode,
                // Room locks selected via checkboxes (entrance always included server-side).
                roomLockCodes: isRoomScope ? values.roomLockCodes : undefined,
                linkedRoomIds: mode === 'standalone' && issueScope !== 'room' ? [] : values.linkedRoomIds,
            };
            const base = { ...identity, ...composedAccess };
            const standaloneAclIds = mode === 'standalone'
                ? issueScope === 'lock' && selectedLock
                    ? [selectedLock.lockIndex]
                    : issueScope === 'lockGroup' && selectedLockGroup
                        ? [selectedLockGroup.groupIndex]
                        : undefined
                : undefined;
            // Non-room access: individual locks (pool, parking, …) + lock groups added to the card.
            const extraLockIndexes = values.extraLockCodes
                .map(code => nonRoomLockOptions.find(option => option.value === code)?.lock.lockIndex)
                .filter((index) => index != null);
            const extraGroupIndexes = values.extraGroupCodes
                .map(code => lockGroupOptions.find(option => option.value === code)?.group.groupIndex)
                .filter((index) => index != null);
            const baseAclIds = context?.lock?.lockIndex != null
                ? [context.lock.lockIndex]
                : context?.lockGroup?.groupIndex != null
                    ? [context.lockGroup.groupIndex]
                    : standaloneAclIds ?? [];
            const mergedAclIds = [...new Set([...baseAclIds, ...extraLockIndexes, ...extraGroupIndexes])];
            const issueParams = {
                ...base,
                cardType: resolvedCardType,
                // Absent, not empty, when nothing was composed — see `composedAccess`.
                ...(roleTemplateOnly || mergedAclIds.length === 0 ? {} : { aclIds: mergedAclIds }),
                ...(context?.visitId ? { visitId: context.visitId } : {}),
            };
            const isReplacement = mode === 'replace' && !!context?.card;
            const payload = isReplacement
                ? await getReplacePayload.mutateAsync({ cardId: context.card.id, params: base })
                : await getIssuePayload.mutateAsync(issueParams);
            const written = await writeCardImage(reader.id, precondition.readerSnapshot.claimId, payload, t);
            if (!written.ok) {
                fail('error', written.message);
                return;
            }
            const { outcomeUnknown, writeReport } = written;
            encodedSuccessfully = true;
            writeOutcomeUnknown = outcomeUnknown;
            // Persist the instant actually signed into the card, including any UID-reuse clamp,
            // without adding a non-contract field to the payload response. The v1 agent returns
            // no write report, so Edge records the card `pending` until the v2 agent contract
            // (plan T16) supplies one. BMS-spec docs/access/cards.md §Write report.
            const validFrom = encodedValidFromIso(payload.payload);
            const authoritativeIdentity = {
                cardUid: writeReport.cardUid,
                chipType: writeReport.chipType,
                writeReport,
            };
            const pending = {
                isReplacement,
                outcomeUnknown,
                replacedCardId: context?.card?.id,
                issueParams: { ...issueParams, ...authoritativeIdentity, validFrom },
                replaceParams: { ...base, ...authoritativeIdentity, validFrom },
            };
            setPendingPersistence(pending);
            await persistWrittenCard(pending);
        }
        catch (error) {
            const detail = cardErrorDetail(error, t);
            if (!encodedSuccessfully) {
                fail('error', detail);
                return;
            }
            const failure = cardSaveFailure(t, error, detail, { outcomeUnknown: writeOutcomeUnknown });
            if (!failure.retrySave)
                setPendingPersistence(null);
            fail('error', failure.message);
        }
    };
    // Room locks as checkboxes — entrance is checked + disabled (always included), others optional.
    const roomLockSection = scopedRoomLocks.length > 0 ? (_jsx(Field, { label: t('cards.roomLocksLabel'), hint: t('cards.roomLocksHint'), children: control => (_jsx(CheckboxGroup, { "aria-labelledby": control['aria-labelledby'], "aria-describedby": control['aria-describedby'], options: scopedRoomLockOptions, value: values.roomLockCodes, onChange: next => setValue('roomLockCodes', next) })) })) : null;
    // Additive picker for access not tied to the room (pool, parking, common areas) + lock groups.
    const nonRoomAccessSection = (_jsxs(_Fragment, { children: [_jsx(SectionDivider, { children: t('cards.additionalAccess') }), _jsx(Field, { label: t('cards.otherLocksLabel'), hint: t('cards.otherLocksHint'), children: control => (_jsx(MultiCombobox, { id: control.id, "aria-describedby": control['aria-describedby'], placeholder: t('cards.addLocks'), options: nonRoomLockOptions.map(({ value, label }) => ({ value, label })), value: values.extraLockCodes, onChange: next => setValue('extraLockCodes', next) })) }), _jsx(Field, { label: t('cards.lockGroupsLabel'), hint: t('cards.lockGroupsHint'), children: control => (_jsx(MultiCombobox, { id: control.id, "aria-describedby": control['aria-describedby'], placeholder: t('cards.addLockGroups'), options: lockGroupOptions.map(({ value, label }) => ({ value, label })), value: values.extraGroupCodes, onChange: next => setValue('extraGroupCodes', next) })) })] }));
    const readerTone = canSubmit ? 'success' : precondition.canOpenIssue ? 'info' : 'warning';
    const readerTitle = canSubmit
        ? t('cards.cardReadyOnReader')
        : !activeReader
            ? t('cards.placeCardOnReader')
            : submitReason ?? t('cards.readerReady');
    return (_jsx(Dialog, { open: open, onOpenChange: next => { if (!next)
            onClose(); }, children: _jsxs(DialogContent, { className: "sm:max-w-[620px]", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { children: [_jsx(CreditCard, { className: "size-4", "aria-hidden": "true" }), t(titleForMode(mode))] }), _jsx(DialogDescription, { className: "sr-only", children: t('cards.dialogDescription') })] }), _jsxs(DialogBody, { className: "flex flex-col gap-4", children: [_jsx(Alert, { tone: readerTone, title: readerTitle, description: mode === 'room' ? undefined : (_jsx(DescriptionList, { items: [
                                    {
                                        label: t('cards.readerLabel'),
                                        value: activeReader
                                            ? activeReader.name
                                            : _jsx("span", { className: "text-muted-foreground", children: t('cards.noCardOnReader') }),
                                    },
                                    {
                                        label: t('cards.chipType'),
                                        value: selectedChipType ? (_jsx(Badge, { variant: selectedChipType === 'mifare_ultralight_aes' ? 'success' : 'info', children: CHIP_TYPE_LABELS[selectedChipType] })) : precondition.lastCard?.card.uid ? (_jsx("span", { className: "text-destructive", children: t('cards.unsupportedOrUnknown') })) : (_jsx("span", { className: "text-muted-foreground", children: t('cards.waitingForTap') })),
                                    },
                                ] })) }), submitError && (_jsx(Alert, { tone: "error", title: submitError, children: pendingPersistence && (_jsx(Button, { className: "mt-2", variant: "outline", onClick: () => { void retryPersistence(); }, disabled: submitPending, children: t('cards.retrySave') })) })), context?.visitId && (_jsx(Alert, { tone: "info", title: context.visitLabel ? t('cards.visitorCardTitleFor', { name: context.visitLabel }) : t('cards.visitorCardTitle'), description: t('cards.visitorCardDescription') })), roleTemplateOnly && (_jsx(Alert, { tone: "info", title: t('cards.roleTemplateOnlyTitle'), description: t('cards.roleTemplateOnlyDescription') })), (errors.cardUid || errors.chipType || errors.cardType) && (_jsx(Alert, { tone: "error", title: errors.cardUid ?? errors.chipType ?? errors.cardType ?? '' })), _jsxs("div", { className: "flex flex-col gap-4", children: [mode !== 'room' && (_jsx(Field, { label: t('cards.cardTypeLabel'), required: true, error: errors.cardType, children: control => (_jsxs(Select, { value: values.cardType, onValueChange: next => setValue('cardType', next), disabled: mode === 'replace', children: [_jsx(SelectTrigger, { id: control.id, "aria-describedby": control['aria-describedby'], "aria-invalid": control.invalid || undefined, children: _jsx(SelectValue, {}) }), _jsx(SelectContent, { children: Object.entries(CARD_TYPE_LABEL_KEYS).map(([value, labelKey]) => (_jsx(SelectItem, { value: value, children: t(labelKey) }, value))) })] })) })), _jsx(Field, { label: t('cards.holderNameLabel'), children: control => (_jsx(Input, { id: control.id, "aria-describedby": control['aria-describedby'], placeholder: t('cards.autoFilledFromGuest'), value: values.holderName ?? '', onChange: event => setValue('holderName', event.target.value) })) }), mode === 'standalone' ? (_jsxs(_Fragment, { children: [!roleTemplateOnly && (_jsx(Field, { label: t('cards.issueForLabel'), required: true, error: errors.issueScope, children: control => (_jsxs(Select, { value: values.issueScope, onValueChange: next => setValue('issueScope', next), children: [_jsx(SelectTrigger, { id: control.id, "aria-describedby": control['aria-describedby'], "aria-invalid": control.invalid || undefined, children: _jsx(SelectValue, {}) }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: "room", children: t('cards.optionRoom') }), _jsx(SelectItem, { value: "lock", children: t('cards.optionLock') }), _jsx(SelectItem, { value: "lockGroup", children: t('cards.optionLockGroup') }), _jsx(SelectItem, { value: "role", children: t('cards.optionRoleTemplate') })] })] })) })), issueScope === 'room' && (_jsxs(_Fragment, { children: [_jsx(Field, { label: t('cards.primaryRoomLabel'), required: true, error: errors.roomCode, children: control => (_jsx(Combobox, { id: control.id, "aria-describedby": control['aria-describedby'], invalid: control.invalid, placeholder: t('cards.selectRoom'), options: roomOptions, value: values.roomCode, onChange: next => setValue('roomCode', next) })) }), roomLockSection, nonRoomAccessSection] })), issueScope === 'lock' && (_jsx(Field, { label: t('cards.lockLabel'), required: true, error: errors.lockCode, children: control => (_jsx(Combobox, { id: control.id, "aria-describedby": control['aria-describedby'], invalid: control.invalid, placeholder: t('cards.selectLock'), options: lockOptions.map(({ value, label }) => ({ value, label })), value: values.lockCode, onChange: next => setValue('lockCode', next) })) })), issueScope === 'lockGroup' && (_jsx(Field, { label: t('cards.lockGroupLabel'), required: true, error: errors.lockGroupCode, children: control => (_jsx(Combobox, { id: control.id, "aria-describedby": control['aria-describedby'], invalid: control.invalid, placeholder: t('cards.selectLockGroup'), options: lockGroupOptions.map(({ value, label }) => ({ value, label })), value: values.lockGroupCode, onChange: next => setValue('lockGroupCode', next) })) })), issueScope === 'role' && (_jsx(Field, { label: t('cards.roleTemplateLabel'), required: true, error: errors.roleTemplateName, children: control => (_jsx(Combobox, { id: control.id, "aria-describedby": control['aria-describedby'], invalid: control.invalid, placeholder: t('cards.selectRoleTemplate'), options: roles.map(role => ({ value: role.roleName, label: role.roleName })), value: values.roleTemplateName, onChange: next => setValue('roleTemplateName', next) })) }))] })) : (_jsxs(_Fragment, { children: [mode !== 'room' && (_jsx(Field, { label: t('cards.roleTemplateLabel'), children: control => (_jsx(Combobox, { id: control.id, "aria-describedby": control['aria-describedby'], placeholder: t('cards.optionalRoleTemplate'), allowClear: true, disabled: mode === 'role', options: roles.map(role => ({ value: role.roleName, label: role.roleName })), value: values.roleTemplateName, onChange: next => setValue('roleTemplateName', next) })) })), mode !== 'room' && (_jsx(Field, { label: t('cards.primaryRoomLabel'), children: control => (_jsx(Combobox, { id: control.id, "aria-describedby": control['aria-describedby'], placeholder: t('cards.optionalPrimaryRoom'), allowClear: true, options: roomOptions, value: values.roomCode, onChange: next => setValue('roomCode', next) })) })), mode === 'room' && (_jsxs(_Fragment, { children: [roomLockSection, nonRoomAccessSection] }))] })), (mode === 'room' || mode === 'replace' || (mode === 'standalone' && issueScope === 'room')) && (_jsx(Field, { label: t('cards.linkedRoomsLabel'), children: control => (_jsx(MultiCombobox, { id: control.id, "aria-describedby": control['aria-describedby'], placeholder: t('cards.linkedRooms'), options: linkedRoomOptions, value: values.linkedRoomIds, onChange: next => setValue('linkedRoomIds', next) })) })), _jsx(Field, { label: t('cards.validUntilLabel'), required: true, error: errors.validUntil, children: control => (_jsx(DateTimeInput, { id: control.id, "aria-label": t('cards.validUntilLabel'), "aria-describedby": control['aria-describedby'], invalid: control.invalid, max: isoToLocalInput(maxValidUntil.toISOString()), value: isoToLocalInput(values.validUntil), onChange: next => setValue('validUntil', localInputToIso(next)) })) })] })] }), _jsxs(DialogFooter, { children: [_jsx(Button, { variant: "outline", onClick: onClose, children: t('kit.cancel') }), _jsxs(Button, { onClick: () => { void handleSubmit(); }, disabled: !!submitReason || submitPending || !!pendingPersistence, title: submitReason, children: [submitPending && _jsx(Loader2, { className: "size-4 animate-spin", "aria-hidden": "true" }), mode === 'replace' ? t('cards.replaceAndEncode') : t('cards.issueAndEncode')] })] })] }) }));
};
export default IssueCardModal;
//# sourceMappingURL=IssueCardModal.js.map